import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { Times } from '@styled-icons/fa-solid/Times';
import { getAuth } from 'firebase/auth';
import { API_URL } from '../../constants/string-constants';
import {
  ModalWrapper,
  ModalContent,
  ModernTitle,
  ModernSubtitle,
} from './shared/ModalBase';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1.5rem;
  right: 1.5rem;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  color: var(--clr-sec);
  opacity: 0.7;
  z-index: 10;

  &:hover {
    color: #ff6b6b;
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ProfileSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ProfileField = styled.div`
  margin-bottom: 1rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--clr-sec);
  margin-bottom: 0.5rem;
  opacity: 0.8;
`;

const FieldValue = styled.div`
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: var(--clr-sec);
  min-height: ${props => props.multiline ? '80px' : 'auto'};
  white-space: ${props => props.multiline ? 'pre-wrap' : 'normal'};
  word-wrap: break-word;
  font-size: 0.95rem;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  color: var(--clr-sec);
  opacity: 0.6;
  font-style: italic;
  font-size: 0.9rem;
`;

const LoadingState = styled.div`
  color: var(--clr-sec);
  opacity: 0.8;
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem;
`;

const ErrorState = styled.div`
  color: #ff6b6b;
  font-size: 0.9rem;
  text-align: center;
  padding: 2rem;
`;

const ViewProfileModal = ({ visible, onClose, userId }) => {
  const [profileData, setProfileData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (visible && userId) {
      loadUserProfile();
    }
  }, [visible, userId]);

  const loadUserProfile = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        setError('Please sign in to view profiles');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_URL}users/profile/${userId}`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to load profile');
      }
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setProfileData(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={visible}
      onRequestClose={handleClose}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={handleClose} />
          <ModernTitle>{profileData?.nickname || 'Anon'}</ModernTitle>
          <ModernSubtitle>
            User Profile
          </ModernSubtitle>

          {isLoading && <LoadingState>Loading profile...</LoadingState>}

          {error && <ErrorState>{error}</ErrorState>}

          {profileData && (
            <ProfileSection>
              <ProfileField>
                <FieldLabel>Bio</FieldLabel>
                <FieldValue multiline>
                  {profileData.bio || <EmptyState>No bio available</EmptyState>}
                </FieldValue>
              </ProfileField>

              {profileData.agePublic && profileData.age && (
                <ProfileField>
                  <FieldLabel>Age</FieldLabel>
                  <FieldValue>{profileData.age} years old</FieldValue>
                </ProfileField>
              )}

              {profileData.pronounsPublic && profileData.pronouns && (
                <ProfileField>
                  <FieldLabel>Pronouns</FieldLabel>
                  <FieldValue>{profileData.pronouns}</FieldValue>
                </ProfileField>
              )}
            </ProfileSection>
          )}
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};

export default ViewProfileModal;